import { ContextKey } from '@pubstack/common/src/contextKey'
import { GamConfig } from '@pubstack/common/src/gam/gamConfig'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContextKeys, useGamConfig, useGamSiteConfig } from '~/api/adm-api.hook'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useSiteWithRoute } from '~/modules/adm/useSiteWithRoute'
import { configurationState } from '~/state'
import { PureAdmGamIntegrationPage } from './PureAdmGamIntegrationPage'

export const AdmGamIntegrationPage: React.FunctionComponent = () => {
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const [contextKeys, setContextKeys] = useState<ContextKey[]>([])
  const [gamConfig, setGamConfig] = useState<GamConfig>({ scopeId: '', networkId: '', sites: [], siteConfigs: [] })
  const { site, siteId } = useSiteWithRoute()
  const breadcrumbsContent = useBreadcrumbs(site ?? 'Google Ad Manager (GAM)')
  const breadcrumbs = siteId ? breadcrumbsContent : <></>
  const toast = useToast()
  const navigate = useNavigate()
  const { byId: gamConfigById } = useGamConfig(null)
  const { byId: gamSiteConfigById } = useGamSiteConfig(null)
  const { all: allContextKeys } = useContextKeys(null)

  const getCurrentGamConfig = async () => {
    const gamConfig = await gamConfigById.get()
    setGamConfig(gamConfig)
  }

  const getContextKeys = async () => {
    const res = await allContextKeys.get()
    setContextKeys(res)
  }

  const getPageData = async () => {
    getCurrentGamConfig()
    getContextKeys()
  }

  const createNetworkId = async (networkId: string) => {
    try {
      const res = await gamConfigById.put(networkId)
      setGamConfig(res)
      toast.success(`The network id has been saved.`)
    } catch (_err) {
      toast.alert('An error occurred when trying to save the network id.')
    }
  }

  const createSiteConfig = async (siteConfig: AdmSiteConfig) => {
    try {
      await gamSiteConfigById.put(siteConfig)
      await getCurrentGamConfig()
      toast.admSuccess(gamConfig.sites.find((site) => site.id === siteConfig.siteId)?.name ?? '')
    } catch (_err) {
      toast.alert(`An error occurred when trying to save the site configuration.`)
    }
  }

  const onNavigateToContextKeys = () => {
    navigate('/monetization/one/context/context-keys')
  }
  const onNavigateToAdUnits = () => {
    navigate('/monetization/one/adunits')
  }

  useEffect(() => {
    // Legitimate data load on mount (async fetch then setState).
    // oxlint-disable-next-line react/set-state-in-effect
    getPageData()
  }, [])

  return (
    <PureAdmGamIntegrationPage
      isLoading={gamConfigById.loading}
      breadcrumbs={breadcrumbs}
      createNetworkId={createNetworkId}
      gamConfig={gamConfig}
      contextKeys={contextKeys}
      createSiteConfig={createSiteConfig}
      onNavigateToContextKeys={onNavigateToContextKeys}
      onNavigateToAdUnits={onNavigateToAdUnits}
      onBackClick={() => navigate(-1)}
      baseCDNUrl={STATIC_ASSETS_CDN}
      fromSiteId={siteId}
    />
  )
}
